import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';

import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import {
  authHeaders,
  deactivateRestaurantUser,
  extractRestaurantId,
  extractRestaurantName,
  fetchUserRestaurants,
  mergeRestaurantUserRows,
  resolveInitialRestaurant,
} from './restaurantApi';
import { fetchActiveSupermarkets } from './groceryApi';
import {
  createStoreUser,
  deactivateStoreUser,
  extractStoreId,
  extractStoreName,
  fetchStoreUsers,
  resolveInitialStore,
  updateStoreUser,
} from './storeUserApi';
import { fetchEntityRoles } from './entityRbacApi';
import { isSupermarketContext } from './entityContext';
import { canManageUsers } from './rbacSession';
import {
  formatAadharDisplay,
  formatAadharDisplayOrFallback,
  formatMobileDisplayOrFallback,
  sanitizeAadharInput,
  sanitizeMobileInput,
  validateAadharNumber,
  validateMobileNumber,
} from './formatDisplay';
import OrdersTablePagination from './OrdersTablePagination';
import SortableTableTh from './SortableTableTh';
import { buildSortHandlers, sortList, str } from './tableSortUtils';

import './css/Inventory.css';
import './css/Menu.css';
import './css/Purchase.css';
import './css/OrdersTablePagination.css';
import './css/UserManagement.css';
import { confirmAction } from './ui/feedback';

const initialForm = {
  firstName: '',
  lastName: '',
  mobile: '',
  email: '',
  aadharNo: '',
  entityRoleIds: [],
  password: '',
  status: 'ACTIVE',
};

const USER_STATUS_OPTIONS = [
  { label: 'Inactive', value: 'INACTIVE' },
  { label: 'Active', value: 'ACTIVE' },
];

function getUserSortValue(row, sortKey) {
  switch (sortKey) {
    case 'id':
      return Number(row.id) || 0;
    case 'firstName':
      return str(row.firstName);
    case 'lastName':
      return str(row.lastName);
    case 'email':
      return str(row.email);
    case 'aadharNo':
      return str(row.aadharNo);
    case 'mobile':
      return str(row.mobile);
    case 'status':
      return str(row.status);
    case 'roles': {
      const fromEntity = (row.entityRoleIds || []).join(', ');
      if (fromEntity) return fromEntity;
      return (row.roles || []).join(', ');
    }
    default:
      return '';
  }
}

function isUserInactive(status) {
  return String(status || '').toUpperCase() === 'INACTIVE';
}

function formatRoleLabel(role, entityRoles = []) {
  const asId = Number(role);
  if (Number.isFinite(asId) && asId > 0) {
    const match = entityRoles.find((r) => Number(r.entityRoleId) === asId);
    if (match?.roleName) return match.roleName;
  }
  const normalized = String(role || '').trim();
  const byName = entityRoles.find(
    (r) => String(r.roleName || '').toLowerCase() === normalized.toLowerCase()
  );
  if (byName?.roleName) return byName.roleName;
  return normalized.replace(/_/g, ' ') || '—';
}

function buildFormFromUserRow(record) {
  if (!record) return { ...initialForm };
  return {
    firstName: String(record.firstName ?? ''),
    lastName: String(record.lastName ?? ''),
    mobile: sanitizeMobileInput(record.mobile ?? ''),
    email: String(record.email ?? ''),
    aadharNo: sanitizeAadharInput(record.aadharNo ?? ''),
    entityRoleIds: [...(record.entityRoleIds || [])],
    password: '',
    status: String(record.status ?? 'ACTIVE').toUpperCase(),
  };
}

const UserManagementAdmin = () => {
  const isStoreContext = isSupermarketContext();
  const allowManageUsers = canManageUsers();
  const [users, setUsers] = useState([]);
  const [entityRoles, setEntityRoles] = useState([]);
  const [restaurantId, setRestaurantId] = useState('');
  const [restaurantName, setRestaurantName] = useState('');
  const [storeId, setStoreId] = useState('');
  const [storeName, setStoreName] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [searchQuery, setSearchQuery] = useState('');
  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize, setUsersPageSize] = useState(10);
  const [usersSort, setUsersSort] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [listMessage, setListMessage] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const userId = user?.userId ?? user?.user_id ?? null;
  const ownerId = isStoreContext ? storeId : restaurantId;
  const ownerName = isStoreContext ? storeName : restaurantName;
  const ownerRequiredMessage = isStoreContext
    ? 'No store selected. Switch store from the profile menu.'
    : 'No restaurant selected. Switch restaurant from the profile menu.';

  const loadEntityRoles = useCallback(
    async (entityId) => {
      if (!entityId) {
        setEntityRoles([]);
        return;
      }
      try {
        const rows = await fetchEntityRoles(entityId, { isStore: isStoreContext });
        setEntityRoles(rows);
      } catch (error) {
        console.error('Error fetching entity roles:', error?.response?.data || error);
        setEntityRoles([]);
      }
    },
    [isStoreContext]
  );

  const fetchUsers = useCallback(
    async (entityId) => {
      if (!entityId) return;

      try {
        setUsersLoading(true);
        if (isStoreContext) {
          const rows = await fetchStoreUsers(entityId);
          setUsers(rows);
        } else {
          const response = await axiosRestaurantInstance.get(
            RESTAURANT_API_PATHS.restaurantUsersByRestaurantId(entityId),
            { headers: authHeaders() }
          );
          const usersArray = response.data?.users || [];
          setUsers(mergeRestaurantUserRows(usersArray));
        }
        setListMessage('');
      } catch (error) {
        console.error('Error fetching users:', error?.response?.data || error);
        setUsers([]);
        const status = error?.response?.status;
        const msg = error?.response?.data?.message || error?.message;
        if (status === 401) {
          setListMessage('Session expired or not signed in. Please sign in again.');
        } else if (status === 802) {
          setListMessage('User service unavailable (802). Please check backend or try again later.');
        } else {
          setListMessage(msg || 'Failed to fetch users. Please try again.');
        }
      } finally {
        setUsersLoading(false);
      }
    },
    [isStoreContext]
  );

  useEffect(() => {
    const initEntity = async () => {
      if (!userId) return;

      try {
        if (isStoreContext) {
          const list = await fetchActiveSupermarkets(userId);
          const storedId = localStorage.getItem('storeId');
          const storedInList =
            storedId && list.some((s) => String(extractStoreId(s)) === String(storedId));

          let store = null;
          if (storedInList) {
            store = list.find((s) => String(extractStoreId(s)) === String(storedId)) || null;
          } else {
            store = await resolveInitialStore(userId, list);
          }

          if (store) {
            const id = String(extractStoreId(store));
            const name = extractStoreName(store);
            setStoreId(id);
            setStoreName(name);
            localStorage.setItem('storeId', id);
            if (name) localStorage.setItem('storeName', name);
            await fetchUsers(id);
            await loadEntityRoles(id);
          }
          return;
        }

        const list = await fetchUserRestaurants(userId);
        const storedId = localStorage.getItem('restaurantId');
        const storedInList =
          storedId && list.some((r) => String(extractRestaurantId(r)) === String(storedId));

        let restaurant = null;
        if (storedInList) {
          restaurant =
            list.find((r) => String(extractRestaurantId(r)) === String(storedId)) || null;
        } else {
          restaurant = await resolveInitialRestaurant(userId, list);
        }

        if (restaurant) {
          const id = String(extractRestaurantId(restaurant));
          const name = extractRestaurantName(restaurant);
          setRestaurantId(id);
          setRestaurantName(name);
          localStorage.setItem('restaurantId', id);
          if (name) localStorage.setItem('restaurantName', name);
          await fetchUsers(id);
          await loadEntityRoles(id);
        }
      } catch (error) {
        console.error('Error resolving entity context:', error.response?.data || error.message);
        setListMessage(
          isStoreContext ? 'Could not load store context.' : 'Could not load restaurant context.'
        );
      }
    };

    initEntity();
  }, [userId, isStoreContext, fetchUsers, loadEntityRoles]);

  const resetForm = () => {
    setForm({ ...initialForm });
    setEditingUser(null);
  };

  const handleOpen = () => {
    if (!allowManageUsers) return;
    setSubmitError('');
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (record) => {
    if (!allowManageUsers) return;
    setSubmitError('');
    setEditingUser(record);
    setForm(buildFormFromUserRow(record));
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setSubmitError('');
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'mobile'
        ? sanitizeMobileInput(value)
        : name === 'aadharNo'
          ? sanitizeAadharInput(value)
          : value,
    }));
  };

  const toggleEntityRole = (entityRoleId) => {
    setForm((prev) => {
      const id = Number(entityRoleId) || entityRoleId;
      const hasRole = prev.entityRoleIds.some((r) => String(r) === String(id));
      return {
        ...prev,
        entityRoleIds: hasRole
          ? prev.entityRoleIds.filter((r) => String(r) !== String(id))
          : [...prev.entityRoleIds, id],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!allowManageUsers) {
      setSubmitError('You do not have permission to manage users.');
      return;
    }

    if (!ownerId) {
      setSubmitError(ownerRequiredMessage);
      return;
    }

    if (!form.firstName.trim()) {
      setSubmitError('First name is required.');
      return;
    }
    if (!form.lastName.trim()) {
      setSubmitError('Last name is required.');
      return;
    }

    const mobileError = validateMobileNumber(form.mobile, { required: true, label: 'Mobile' });
    if (mobileError) {
      setSubmitError(mobileError);
      return;
    }

    if (!form.email.trim()) {
      setSubmitError('Email is required.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setSubmitError('Enter a valid email address.');
      return;
    }
    const aadharError = validateAadharNumber(form.aadharNo);
    if (aadharError) {
      setSubmitError(aadharError);
      return;
    }
    if (!form.entityRoleIds.length) {
      setSubmitError('Select at least one role.');
      return;
    }
    if (!editingUser && (!form.password || form.password.length < 6)) {
      setSubmitError('Password is required and must be at least 6 characters.');
      return;
    }

    setIsSubmitting(true);

    const selectedRoleNames = entityRoles
      .filter((role) =>
        form.entityRoleIds.some((id) => String(id) === String(role.entityRoleId))
      )
      .map((role) => role.roleName)
      .filter(Boolean);

    const basePayload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      mobile: form.mobile.trim(),
      email: form.email.trim(),
      aadharNo: sanitizeAadharInput(form.aadharNo),
      entityRoleIds: form.entityRoleIds,
      roleNames: selectedRoleNames,
      createdById: userId,
      fcmToken: null,
    };

    if (isStoreContext) {
      basePayload.storeId = ownerId;
      basePayload.storeName = ownerName;
    } else {
      basePayload.restaurantId = ownerId;
      basePayload.restaurantName = ownerName;
    }

    if (!editingUser && form.password) {
      basePayload.password = form.password;
    }

    if (editingUser && isUserInactive(editingUser.status)) {
      basePayload.status = form.status;
    }

    try {
      if (editingUser) {
        const reactivated =
          isUserInactive(editingUser.status) && form.status === 'ACTIVE';
        if (isStoreContext) {
          await updateStoreUser(editingUser.id, {
            ...basePayload,
            updatedById: userId,
          });
        } else {
          await axiosRestaurantInstance.put(
            RESTAURANT_API_PATHS.restaurantUserById(editingUser.id),
            { ...basePayload, updatedById: userId },
            { headers: authHeaders() }
          );
        }
        alert(reactivated ? 'User activated successfully' : 'User updated successfully');
      } else if (isStoreContext) {
        await createStoreUser(basePayload);
        alert('User added successfully');
      } else {
        await axiosRestaurantInstance.post(RESTAURANT_API_PATHS.restaurantsUsers, basePayload, {
          headers: authHeaders(),
        });
        alert('User added successfully');
      }

      await fetchUsers(ownerId);
      handleClose();
    } catch (error) {
      console.error('Error submitting user:', error.response?.data || error.message);

      const errorString = JSON.stringify(error.response?.data || error.message || '');

      if (
        errorString.includes('Duplicate entry') &&
        (errorString.includes('email') || errorString.includes('Email'))
      ) {
        setSubmitError('This email address is already registered. Please use a different email.');
      } else if (error.response?.data?.message) {
        setSubmitError(error.response.data.message);
      } else if (error.response?.data?.error) {
        setSubmitError(error.response.data.error);
      } else {
        setSubmitError('Operation failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!allowManageUsers) {
      alert('You do not have permission to manage users.');
      return;
    }
    const record = users.find((u) => String(u.id) === String(id));
    if (!record) {
      alert('User not found.');
      return;
    }
    if (!ownerId) {
      alert(ownerRequiredMessage);
      return;
    }
    if (!userId) {
      alert('Please sign in again.');
      return;
    }

    const ok = await confirmAction({
      title: 'Deactivate user?',
      message: 'Deactivate this user? They will remain in the list with status INACTIVE.',
      confirmLabel: 'Deactivate',
      danger: true,
    });
    if (!ok) return;

    try {
      const response = isStoreContext
        ? await deactivateStoreUser(id, {
            storeId: ownerId,
            storeName: ownerName,
            updatedById: userId,
            userRow: record,
          })
        : await deactivateRestaurantUser(id, {
            restaurantId: ownerId,
            restaurantName: ownerName,
            updatedById: userId,
            userRow: record,
          });
      if (response.status === 200 || response.status === 204) {
        alert(response.data?.message || 'User deactivated successfully');
        await fetchUsers(ownerId);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      const status = error?.response?.status;
      if (error?.code === 'ERR_NETWORK' || !error?.response) {
        alert(
          'Network error while deactivating user. Check that the API server is running and reachable.'
        );
      } else if (status === 401) {
        alert('Session expired or not authorized. Please sign in again.');
      } else {
        alert(
          error.response?.data?.message ||
            error.response?.data?.error ||
            'Failed to deactivate user'
        );
      }
    }
  };

  const searchQueryTrimmed = searchQuery.trim();

  const filteredUsers = useMemo(() => {
    const q = searchQueryTrimmed.toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [
        u.id,
        u.firstName,
        u.lastName,
        u.email,
        u.mobile,
        u.aadharNo,
        u.status,
        ...(u.roles || []),
      ].some((v) => String(v ?? '').toLowerCase().includes(q))
    );
  }, [users, searchQueryTrimmed]);

  useEffect(() => {
    setUsersPage(1);
    setUsersSort(null);
  }, [searchQueryTrimmed]);

  const sortedUsers = useMemo(
    () => sortList(filteredUsers, usersSort, getUserSortValue),
    [filteredUsers, usersSort]
  );

  const { handleSortAsc: handleUsersSortAsc, handleSortDesc: handleUsersSortDesc } =
    buildSortHandlers(setUsersSort, () => setUsersPage(1));

  const usersTotalPages = Math.max(1, Math.ceil(sortedUsers.length / usersPageSize));
  const usersPageSafe = Math.min(Math.max(1, usersPage), usersTotalPages);

  useEffect(() => {
    setUsersPage((p) => Math.min(p, usersTotalPages));
  }, [usersTotalPages, usersPageSize]);

  const usersPageStart = (usersPageSafe - 1) * usersPageSize;
  const pagedUsers = sortedUsers.slice(usersPageStart, usersPageStart + usersPageSize);

  const handleUsersPageChange = (nextPage) => {
    setUsersPage(nextPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUsersPageSizeChange = (nextSize) => {
    setUsersPageSize(nextSize);
    setUsersPage(1);
  };

  const colCount = 9;

  return (
    <div className="menu inventory-page purchase-page">
      <div className="inventory-supplier-panel user-management-panel">
        {listMessage ? <p className="inventory-status">{listMessage}</p> : null}

        {!ownerId ? <p className="inventory-status">{ownerRequiredMessage}</p> : null}

        {ownerId ? (
          <>
            {!allowManageUsers ? (
              <p className="inventory-status">
                You can view users but need user-manage permission to create or edit them.
              </p>
            ) : null}
            <div className="purchase-content-card">
              <div className="inventory-list-toolbar menu-toolbar">
                <div className="menu-toolbar-row user-management-toolbar-row">
                  <div className="menu-food-filter-wrap">
                    <input
                      type="text"
                      inputMode="search"
                      autoComplete="off"
                      className="menu-food-filter-input"
                      placeholder="Search users…"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      aria-label="Search users"
                      role="searchbox"
                    />
                    {searchQuery.trim() !== '' ? (
                      <button
                        type="button"
                        className="menu-food-filter-clear"
                        aria-label="Clear search"
                        onClick={() => setSearchQuery('')}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    ) : null}
                  </div>
                  <div className="menu-toolbar-spacer" />
                  <div className="purchase-toolbar-actions">
                    {allowManageUsers ? (
                      <button
                        type="button"
                        className="purchase-btn purchase-btn--create"
                        onClick={handleOpen}
                      >
                        Add User
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="menu-table-wrapper">
                <table className="menu-table">
                  <thead>
                    <tr>
                      <SortableTableTh
                        label="User ID"
                        sortKey="id"
                        activeSortKey={usersSort?.key}
                        sortDirection={usersSort?.direction}
                        onSortAsc={handleUsersSortAsc}
                        onSortDesc={handleUsersSortDesc}
                      />
                      <SortableTableTh
                        label="First Name"
                        sortKey="firstName"
                        activeSortKey={usersSort?.key}
                        sortDirection={usersSort?.direction}
                        onSortAsc={handleUsersSortAsc}
                        onSortDesc={handleUsersSortDesc}
                      />
                      <SortableTableTh
                        label="Last Name"
                        sortKey="lastName"
                        activeSortKey={usersSort?.key}
                        sortDirection={usersSort?.direction}
                        onSortAsc={handleUsersSortAsc}
                        onSortDesc={handleUsersSortDesc}
                      />
                      <SortableTableTh
                        label="Email"
                        sortKey="email"
                        activeSortKey={usersSort?.key}
                        sortDirection={usersSort?.direction}
                        onSortAsc={handleUsersSortAsc}
                        onSortDesc={handleUsersSortDesc}
                      />
                      <SortableTableTh
                        label="Aadhar No"
                        sortKey="aadharNo"
                        activeSortKey={usersSort?.key}
                        sortDirection={usersSort?.direction}
                        onSortAsc={handleUsersSortAsc}
                        onSortDesc={handleUsersSortDesc}
                      />
                      <SortableTableTh
                        label="Mobile#"
                        sortKey="mobile"
                        className="table-col-mobile"
                        activeSortKey={usersSort?.key}
                        sortDirection={usersSort?.direction}
                        onSortAsc={handleUsersSortAsc}
                        onSortDesc={handleUsersSortDesc}
                      />
                      <SortableTableTh
                        label="Status"
                        sortKey="status"
                        activeSortKey={usersSort?.key}
                        sortDirection={usersSort?.direction}
                        onSortAsc={handleUsersSortAsc}
                        onSortDesc={handleUsersSortDesc}
                      />
                      <SortableTableTh
                        label="Roles"
                        sortKey="roles"
                        activeSortKey={usersSort?.key}
                        sortDirection={usersSort?.direction}
                        onSortAsc={handleUsersSortAsc}
                        onSortDesc={handleUsersSortDesc}
                      />
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersLoading ? (
                      <tr>
                        <td colSpan={colCount} className="inventory-items-hint inventory-table-empty-cell">
                          Loading users…
                        </td>
                      </tr>
                    ) : users.length === 0 ? (
                      <tr>
                        <td colSpan={colCount} className="inventory-items-hint inventory-table-empty-cell">
                          No users yet.
                        </td>
                      </tr>
                    ) : sortedUsers.length === 0 ? (
                      <tr>
                        <td colSpan={colCount} className="inventory-items-hint inventory-table-empty-cell">
                          No users match your search.
                        </td>
                      </tr>
                    ) : (
                      pagedUsers.map((record) => {
                        const statusValue = String(record.status || '').toUpperCase();
                        return (
                          <tr key={record.id}>
                            <td>{record.id}</td>
                            <td>{record.firstName || '—'}</td>
                            <td>{record.lastName || '—'}</td>
                            <td>{record.email || '—'}</td>
                            <td>{formatAadharDisplayOrFallback(record.aadharNo)}</td>
                            <td className="table-col-mobile">
                              {formatMobileDisplayOrFallback(record.mobile, '—')}
                            </td>
                            <td>
                              {statusValue ? (
                                <span
                                  className={`user-status-pill user-status-pill--${statusValue.toLowerCase()}`}
                                >
                                  {statusValue}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td>
                              <div className="user-role-list">
                                {(record.entityRoleIds || []).length > 0
                                  ? (record.entityRoleIds || []).map((roleId) => (
                                      <span key={roleId} className="user-role-pill">
                                        {formatRoleLabel(roleId, entityRoles)}
                                      </span>
                                    ))
                                  : (record.roles || []).map((role) => (
                                      <span key={role} className="user-role-pill">
                                        {formatRoleLabel(role, entityRoles)}
                                      </span>
                                    ))}
                              </div>
                            </td>
                            <td className="menu-table-actions-cell">
                              {allowManageUsers ? (
                                <>
                                  <button
                                    type="button"
                                    className="action-button"
                                    title="Edit user"
                                    aria-label="Edit user"
                                    onClick={() => handleEdit(record)}
                                  >
                                    <FontAwesomeIcon icon={faEdit} />
                                  </button>
                                  {!isUserInactive(record.status) ? (
                                    <button
                                      type="button"
                                      className="action-button delete-button"
                                      title="Deactivate user"
                                      aria-label="Deactivate user"
                                      onClick={() => handleDelete(record.id)}
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                  ) : null}
                                </>
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {!usersLoading && sortedUsers.length > 0 ? (
                <OrdersTablePagination
                  currentPage={usersPageSafe}
                  totalPages={usersTotalPages}
                  totalItems={sortedUsers.length}
                  pageSize={usersPageSize}
                  onPageChange={handleUsersPageChange}
                  onPageSizeChange={handleUsersPageSizeChange}
                  itemLabel="users"
                  ariaLabel="User management table pagination"
                />
              ) : null}
            </div>
          </>
        ) : null}

        {showModal ? (
          <div className="inventory-modal-overlay">
            <div
              className="inventory-modal inventory-user-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="user-modal-title"
            >
              <div className="inventory-modal-header">
                <h4 id="user-modal-title">
                  {editingUser ? `Edit User — ID ${editingUser.id}` : 'Add User'}
                </h4>
                <button
                  type="button"
                  className="menu-template-modal-close"
                  aria-label="Close"
                  onClick={handleClose}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>

              {submitError ? <p className="inventory-status">{submitError}</p> : null}

              <form className="inventory-form inventory-form--user-modal" onSubmit={handleSubmit}>
                <div className="inventory-form-group">
                  <label htmlFor="user-first-name" className="inventory-form-label--required">
                    First name
                  </label>
                  <input
                    id="user-first-name"
                    type="text"
                    name="firstName"
                    className="add-menu-form-field"
                    value={form.firstName}
                    onChange={handleChange}
                    autoComplete="off"
                    required
                  />
                </div>
                <div className="inventory-form-group">
                  <label htmlFor="user-last-name" className="inventory-form-label--required">
                    Last name
                  </label>
                  <input
                    id="user-last-name"
                    type="text"
                    name="lastName"
                    className="add-menu-form-field"
                    value={form.lastName}
                    onChange={handleChange}
                    autoComplete="off"
                    required
                  />
                </div>
                <div className="inventory-form-group">
                  <label htmlFor="user-mobile" className="inventory-form-label--required">
                    Mobile
                  </label>
                  <input
                    id="user-mobile"
                    type="tel"
                    name="mobile"
                    className="add-menu-form-field"
                    value={form.mobile}
                    onChange={handleChange}
                    autoComplete="off"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit mobile number"
                    required
                  />
                </div>
                <div className="inventory-form-group">
                  <label htmlFor="user-email" className="inventory-form-label--required">
                    Email
                  </label>
                  <input
                    id="user-email"
                    type="email"
                    name="email"
                    className="add-menu-form-field"
                    value={form.email}
                    onChange={handleChange}
                    autoComplete="off"
                    required
                  />
                </div>
                <div className="inventory-form-group">
                  <label htmlFor="user-aadhar" className="inventory-form-label--required">
                    Aadhar number
                  </label>
                  <input
                    id="user-aadhar"
                    type="text"
                    name="aadharNo"
                    className="add-menu-form-field"
                    value={formatAadharDisplay(form.aadharNo)}
                    onChange={handleChange}
                    autoComplete="off"
                    inputMode="numeric"
                    maxLength={14}
                    placeholder="1111 1111 1111"
                    required
                  />
                </div>
                {!editingUser ? (
                  <div className="inventory-form-group">
                    <label htmlFor="user-password" className="inventory-form-label--required">
                      Password
                    </label>
                    <input
                      id="user-password"
                      type="password"
                      name="password"
                      className="add-menu-form-field"
                      value={form.password}
                      onChange={handleChange}
                      autoComplete="new-password"
                      minLength={6}
                      required
                    />
                  </div>
                ) : isUserInactive(editingUser.status) ? (
                  <div className="inventory-form-group">
                    <label htmlFor="user-status" className="inventory-form-label--required">
                      Status
                    </label>
                    <select
                      id="user-status"
                      name="status"
                      className="add-menu-form-field"
                      value={form.status}
                      onChange={handleChange}
                    >
                      {USER_STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                <div className="inventory-form-group inventory-form-span-full user-role-checkboxes">
                  <span className="user-role-checkboxes-label inventory-form-label--required">
                    Roles
                  </span>
                  <div className="user-role-checkboxes-row">
                    {entityRoles.length === 0 ? (
                      <span className="inventory-items-hint">
                        No entity roles loaded. Open Roles admin or retry after the API is available.
                      </span>
                    ) : (
                      entityRoles.map((role) => (
                        <label key={role.entityRoleId} className="inventory-checkbox-label">
                          <input
                            type="checkbox"
                            checked={form.entityRoleIds.some(
                              (id) => String(id) === String(role.entityRoleId)
                            )}
                            onChange={() => toggleEntityRole(role.entityRoleId)}
                          />
                          <span>{role.roleName}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
                <div className="inventory-form-actions inventory-form-span-full">
                  <button type="submit" className="add-button" disabled={isSubmitting}>
                    {isSubmitting
                      ? 'Saving…'
                      : editingUser
                        ? 'Update User'
                        : 'Add User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default UserManagementAdmin;
